import React from "react";
import { toast } from "react-toastify";

// Every deploy deletes the previous build's files, so a tab opened before the
// deploy fails as soon as it lazy-loads one of them.
const STALE_BUILD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|is not a valid JavaScript MIME type/i;

const RELOAD_TIMESTAMP_KEY = "staleBuildReloadAt";
// A failure this soon after our own reload means fresh files are not available
// either, so reloading again would only loop.
const RELOAD_GUARD_MS = 60 * 1000;
const NOTICE_TOAST_ID = "stale-build-notice";

let pendingPageImports = 0;

export function isStaleBuildError(error) {
  return STALE_BUILD_ERROR.test(error?.message ?? "");
}

function reloadOnce() {
  if (navigator.onLine === false) return false;
  try {
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY));
    if (Date.now() - lastReloadAt < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

function showReloadNotice() {
  toast.warning(
    <div>
      <p className="mb-2">
        Diese Funktion konnte nicht geladen werden, vermutlich wegen eines Updates. Bitte Eingaben
        sichern und die Seite neu laden.
      </p>
      <button type="button" className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>
        Neu laden
      </button>
    </div>,
    { toastId: NOTICE_TOAST_ID, autoClose: false, closeOnClick: false, draggable: false },
  );
}

/**
 * For code loaded while opening a page. Navigating has already discarded the
 * previous page's state, so reloading costs no input. If the reload is not
 * possible the error reaches the root boundary, which shows ErrorFallback.
 */
export function pageImport(importer) {
  return async () => {
    pendingPageImports += 1;
    try {
      return await importer();
    } catch (error) {
      if (isStaleBuildError(error) && reloadOnce()) return new Promise(() => {});
      throw error;
    } finally {
      pendingPageImports -= 1;
    }
  };
}

export function lazyPage(importer) {
  return React.lazy(pageImport(importer));
}

/**
 * For dialogs and panels loaded inside an open page, where a reload or the root
 * boundary would destroy unsaved input: the page stays as it is and the user
 * decides when to reload.
 */
export function lazyInPage(importer) {
  return React.lazy(() =>
    importer().catch((error) => {
      if (!isStaleBuildError(error)) throw error;
      showReloadNotice();
      return new Promise(() => {});
    }),
  );
}

// Covers imports without a wrapper (jsPDF, ExcelJS, Quill inside PrimeReact).
// preventDefault stays off so that their callers still get the rejection.
export function initStaleBuildHandling() {
  window.addEventListener("vite:preloadError", (event) => {
    if (pendingPageImports === 0 && isStaleBuildError(event.payload)) showReloadNotice();
  });
}
